import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiErrorMessage } from '../../utils/apiError';
import mcpServiceService from '../../services/mcpServiceService';
import Button from '../shared/Button';
import Toggle from '../shared/Toggle';
import SenderDomainCard from './SenderDomainCard';
import './McpServicePage.css';

// MCP AS A SERVICE — THE CHAIN'S SETTINGS, on Chain Settings › MCP (moved off the MCP as a Service screen's Chain tab,
// 2026-10-19, so a chain's settings are all in one place): who may connect (approved assistants / open), the rate
// limits, the chain-wide kill switch, and the chain's own sending domain for guest emails. Per-hotel settings, terms,
// content and usage stay on /mcp-service.
//
// Same data and the same calls as that screen (mcpServiceService.getAccount / saveChain / …SenderDomain); the server
// decides who may change it (canEditChain) and the screen only follows it.

export default function McpChainSettings({ accountId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!accountId) return;
    setError(null);
    try { setData(await mcpServiceService.getAccount(accountId)); } catch (e) { setError(apiErrorMessage(e)); }
  }, [accountId]);
  useEffect(() => { setData(null); load(); }, [load]);

  const act = async (what, fn) => {
    setBusy(what);
    setError(null);
    try { setData(await fn()); } catch (e) { setError(apiErrorMessage(e)); } finally { setBusy(null); }
  };

  return (
    <div className="mcps-page mcps-embedded">
      <p className="mcps-sub">AI assistants searching, quoting and booking this chain&rsquo;s hotels. Each hotel&rsquo;s own
        settings, terms, content and usage are on <Link to="/mcp-service">MCP as a Service</Link>.</p>
      {error && <div className="os-msg os-msg-error">{error}</div>}
      {data && <ChainCards data={data} busy={busy} act={act} accountId={accountId} />}
      {!data && !error && <p>Loading…</p>}
    </div>
  );
}

function ChainCards({ data, busy, act, accountId }) {
  const c = data.chain || {};
  const edit = data.canEditChain;
  const [form, setForm] = useState({});
  useEffect(() => {
    setForm({
      access_mode: c.access_mode || 'approved', open_scope: c.open_scope || 'read',
      rate_limit_per_minute: c.rate_limit_per_minute || 60, bookings_per_hour: c.bookings_per_hour || 10,
    });
  }, [data]);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="mcps-section">
      <div className="mcps-card">
        <div className="mcps-card-head">
          <h3>Service</h3>
          <Toggle on={(c.service_status || 'ACTIVE') === 'ACTIVE'} onLabel="Live" offLabel="Paused" disabled={!edit}
                  busy={busy === 'status'} title={edit ? undefined : 'A chain admin can change this'}
                  onChange={(on) => act('status', () => mcpServiceService.saveChain(accountId, { service_status: on ? 'ACTIVE' : 'PAUSED' }))} />
        </div>
        <p className="mcps-meta">Paused refuses every assistant at every hotel in the chain, straight away.</p>
        <div className="mcps-grid">
          <label>Who may connect
            <select value={form.access_mode || 'approved'} onChange={set('access_mode')} disabled={!edit}>
              <option value="approved">Approved assistants only</option>
              <option value="open">Any assistant (no sign-in)</option>
            </select>
          </label>
          <label>Open assistants may
            <select value={form.open_scope || 'read'} onChange={set('open_scope')} disabled={!edit || form.access_mode !== 'open'}>
              <option value="read">Search and read only</option>
              <option value="booking">Also book and cancel</option>
            </select>
          </label>
          <label>Calls per minute (per assistant / IP)
            <input type="number" min={1} max={1000} value={form.rate_limit_per_minute || ''} onChange={set('rate_limit_per_minute')} disabled={!edit} />
          </label>
          <label>Bookings per hour (per assistant / IP)
            <input type="number" min={1} max={1000} value={form.bookings_per_hour || ''} onChange={set('bookings_per_hour')} disabled={!edit} />
          </label>
        </div>
        {edit && <Button variant="primary" disabled={!!busy} onClick={() => act('chain', () => mcpServiceService.saveChain(accountId, {
          ...form, rate_limit_per_minute: Number(form.rate_limit_per_minute), bookings_per_hour: Number(form.bookings_per_hour) }))}>
          {busy === 'chain' ? 'Saving…' : 'Save'}</Button>}
      </div>

      <SenderDomainCard domain={data.senderDomain} canEdit={edit} busy={busy} run={act} ownerLabel="chain"
                        onStart={(d) => mcpServiceService.startSenderDomain(accountId, d)}
                        onCheck={() => mcpServiceService.checkSenderDomain(accountId)}
                        onRemove={() => mcpServiceService.removeSenderDomain(accountId)} />

    </div>
  );
}
